/**
 * Other Profile Page
 * View another user's profile with photo gallery, info, and tabbed content
 * Similar to ProfileScreen but view-only with Follow/Message actions
 */

import { useQuery } from "@tanstack/react-query"
import { Search, X } from "lucide-react"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { CoachingTabContent } from "../components/CoachingTabContent"
import { CommunitiesTabContent } from "../components/CommunitiesTabContent"
import { FollowersTabContent } from "../components/FollowersTabContent"
import { FollowingTabContent } from "../components/FollowingTabContent"
import { PracticingTabContent } from "../components/PracticingTabContent"
import { ProfileInfoSectionDetail } from "../components/ProfileInfoSection.detail"
import { ProfilePhotoGallery } from "../components/ProfilePhotoGallery"
import { TeachingTabContent } from "../components/TeachingTabContent"
import { useFollowRealtime } from "../hooks/useFollowRealtime"
import { useIntentions } from "../hooks/useIntentions"
import { useProfileStatistics } from "../hooks/useProfileStatistics"
import { useProfileVideo } from "../hooks/useProfileVideo"
import { getOtherProfile } from "../services/user.service"
import type { ProfileDetail } from "../types/profile.types"

type ProfileTab = "teaching" | "practicing" | "sessions" | "communities" | "followers"

export function OtherProfileScreen() {
  const { t } = useTranslation("profile")
  const location = useLocation()
  const defaultTab = useMemo((): ProfileTab => {
    if (location.hash === "#sessions") return "sessions"
    return "teaching"
  }, [location.hash])
  const [activeTab, setActiveTab] = React.useState<ProfileTab>(defaultTab)
  const [activeFollowerSubTab, setActiveFollowerSubTab] = useState<
    "followers-list" | "following-list"
  >("followers-list")
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const { userId } = useParams<{ userId: string }>()

  useEffect(() => {
    if (location.hash === "#sessions") {
      const el = document.getElementById("profile-tabs-content")
      el?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [location.hash])

  useFollowRealtime()

  const currentUser = useAuthStore(state => state.user)
  const navigate = useNavigate()
  const tabsRef = useRef<HTMLDivElement>(null)

  // Redirect to editable profile if viewing own profile
  useEffect(() => {
    if (userId === currentUser?.id) {
      navigate("/profile", { replace: true })
    }
  }, [userId, currentUser?.id, navigate])

  // Fetch other user's profile data
  const {
    data: profileResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["profile", userId],
    queryFn: () => getOtherProfile(userId || ""),
    enabled: !!userId,
  })

  // Fetch profile statistics
  const { data: statsResponse } = useProfileStatistics(userId || "")
  const statistics = statsResponse?.data

  // Fetch intro video for other profile
  const { data: videoData } = useProfileVideo(userId || "")

  // Fetch intentions for other user
  const { intentions } = useIntentions(userId || "")
  const checkedIntentionTitles = intentions.filter(i => i.is_checked).map(i => i.title)

  const handleFollowersClick = () => {
    setActiveTab("followers")
    setActiveFollowerSubTab("followers-list")
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  const handleFollowingClick = () => {
    setActiveTab("followers")
    setActiveFollowerSubTab("following-list")
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-stone">{t("loadingProfileOther")}</p>
      </div>
    )
  }

  // Handle error state
  if (isError || !profileResponse?.user) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-5">
        <p className="text-red mb-4">{t("errorLoadingProfile")}</p>
        <p className="text-stone text-sm">
          {error instanceof Error ? error.message : t("profileNotFound")}
        </p>
      </div>
    )
  }

  const user = profileResponse.user

  // Build profile data from API response
  const profile: ProfileDetail = {
    id: user.id,
    name: user.name || "User",
    username: user.username || "",
    bio: user.bio || "",
    city: user.city || "",
    state: user.state || "",
    credentials: [], // Not available in database
    tags: user.tags?.map(t => t.name) || [],
    photos: (() => {
      if (!user.profile_photos || user.profile_photos.length === 0) {
        return []
      }

      const coverPhoto = user.profile_photos.find(p => p.is_cover_image || p.isCover)
      const otherPhotos = user.profile_photos
        .filter(p => !(p.is_cover_image || p.isCover))
        .map(p => p.filePath)
        .slice(0, 2)

      return [...(coverPhoto ? [coverPhoto.filePath] : []), ...otherPhotos]
    })(),
    profilePhoto: user.profile_photos?.find(p => p.is_cover_image || p.isCover)?.filePath,
    galleryPhotos:
      user.profile_photos
        ?.filter(p => {
          // Filter out videos - only show images in gallery
          const isVideo = p.file_type?.startsWith("video")
          // Exclude cover photos and videos
          const isCover = p.is_cover_image || p.isCover
          return !isVideo && !isCover
        })
        .map(p => p.filePath)
        .slice(0, 3) || [],
    followersCount: statistics?.followersCount ?? 0,
    followingCount: statistics?.followingCount ?? 0,
    socials: {
      website: user.website_url || "",
      twitter: user.twitter_url || "",
      instagram: user.instagram_url || "",
      facebook: user.facebook_url || "",
    },
    // Use real statistics from API, or show 0 while loading
    practicingCount: statistics?.practicing ?? 0,
    teachingCount: statistics?.teachings ?? 0,
    coachingCount: statistics?.coachings?.created ?? 0,
    communitiesCount: statistics?.communities ?? 0,
    // Follow status from profile API response
    isFollowing: user.isFollowing,
    isPending: user.isPending,
  }

  // Visibility access from API response
  // If teaching_rituals_visibility_access is false, hide both Teaching AND Coaching tabs
  const canViewTeaching = user.teaching_rituals_visibility_access ?? true
  const canViewPracticing = user.practice_rituals_visibility_access ?? true

  // Determine available tabs based on visibility
  const availableTabs = [
    ...(canViewTeaching ? [{ key: "teaching" as const, count: profile.teachingCount }] : []),
    ...(canViewPracticing ? [{ key: "practicing" as const, count: profile.practicingCount }] : []),
    ...(canViewTeaching ? [{ key: "sessions" as const, count: profile.coachingCount ?? 0 }] : []),
    { key: "communities" as const, count: profile.communitiesCount },
    { key: "followers" as const, count: profile.followersCount },
  ]

  // Get effective active tab - switch to first available if current is hidden
  const effectiveActiveTab = availableTabs.some(t => t.key === activeTab)
    ? activeTab
    : (availableTabs[0]?.key ?? "communities")

  return (
    <div className="bg-background min-h-screen md:pt-8 md:mt-4">
      <div className="max-w-360 mx-auto px-5 md:px-17.5">
        {/* Photo Gallery */}
        <ProfilePhotoGallery photos={profile.galleryPhotos} className="mt-1 md:block hidden" />

        {/* Profile Info + Socials */}
        <ProfileInfoSectionDetail
          profile={profile}
          className="mt-7.5 md:mt-11"
          isOtherProfile
          userId={userId}
          introVideoUrl={videoData?.filePath}
          intentions={checkedIntentionTitles}
          onFollowersClick={handleFollowersClick}
          onFollowingClick={handleFollowingClick}
        />
      </div>

      {/* Tabs */}
      <Tabs
        value={effectiveActiveTab}
        onValueChange={v => setActiveTab(v as ProfileTab)}
        className="mt-5"
      >
        <div ref={tabsRef} className="max-w-360 mx-auto w-full px-5 md:px-17.5">
          <div className="flex md:border-b-1 md:items-center md:justify-between">
            <div className="overflow-x-auto -mx-5 px-5 lg:mx-0 lg:px-0 scrollbar-hide">
              <TabsList
                variant="default"
                className="!h-auto text-body gap-0 p-0 flex-nowrap lg:flex-wrap lg:overflow-visible"
              >
                {canViewTeaching && (
                  <TabsTrigger
                    value="teaching"
                    className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                  >
                    {t("teaching.title")} ({profile.teachingCount})
                  </TabsTrigger>
                )}
                {canViewPracticing && (
                  <TabsTrigger
                    value="practicing"
                    className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                  >
                    {t("practicing.title")} ({profile.practicingCount})
                  </TabsTrigger>
                )}
                {canViewTeaching && (
                  <TabsTrigger
                    value="sessions"
                    className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                  >
                    {t("coaching.title")} ({profile.coachingCount ?? 0})
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="communities"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("communities.title")} ({profile.communitiesCount})
                </TabsTrigger>
                <TabsTrigger
                  value="followers"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("followers")} ({profile.followersCount})
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="hidden lg:flex items-center md:justify-end">
              {/* Always show search bar on small screens, toggle on md+ */}
              <div className="hidden md:flex items-center md:justify-end">
                {showSearch ? (
                  <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-2 py-1.5">
                    <Search className="w-4 h-4 text-ash shrink-0" />
                    <Input
                      type="text"
                      placeholder={t("searchPlaceholder")}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-6 w-6"
                      onClick={() => {
                        setShowSearch(false)
                        setSearchQuery("")
                      }}
                      aria-label="Close search"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setShowSearch(true)}
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5 text-body" />
                  </Button>
                )}
              </div>
              {/* Always visible search bar on small screens */}
              <div className="flex md:hidden items-center gap-2 w-full mt-10">
                <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5 w-full">
                  <Search className="w-4 h-4 text-ash shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="profile-tabs-content" className="md:bg-card bg-transparent">
          <div className="max-w-360 mx-auto w-full md:px-17.5 py-5 md:py-7">
            {canViewTeaching && (
              <TabsContent value="teaching">
                <TeachingTabContent userId={user.id} searchQuery={searchQuery} />
              </TabsContent>
            )}

            {canViewPracticing && (
              <TabsContent value="practicing">
                <PracticingTabContent userId={user.id} searchQuery={searchQuery} />
              </TabsContent>
            )}

            {canViewTeaching && (
              <TabsContent value="sessions">
                <CoachingTabContent userId={user.id} searchQuery={searchQuery} />
              </TabsContent>
            )}

            <TabsContent value="communities">
              <CommunitiesTabContent
                totalCount={profile.communitiesCount}
                userId={user.id}
                searchQuery={searchQuery}
              />
            </TabsContent>

            <TabsContent value="followers">
              <Tabs
                value={activeFollowerSubTab}
                onValueChange={v =>
                  setActiveFollowerSubTab(v as "followers-list" | "following-list")
                }
              >
                <TabsList variant="default" className="mb-6">
                  <h1 className="text-[18px] leading-[30px] font-monteserat font-semibold uppercase">
                    {t("followersTab.title")}
                  </h1>
                  <div className="h-7.5 w-[1px] bg-[#F2F0EB1A] mx-1.75" />
                  <TabsTrigger
                    value="followers-list"
                    className="rounded-none p-0 pr-1.5 text-sm text-[#ffffffb3] font-normal data-[state=active]:font-semibold data-[state=active]:text-white cursor-pointer uppercase"
                  >
                    {t("followers")} ({profile.followersCount})
                  </TabsTrigger>
                  <TabsTrigger
                    value="following-list"
                    className="rounded-none p-0 pl-1.5 text-sm text-[#ffffffb3] font-normal data-[state=active]:font-semibold data-[state=active]:text-white cursor-pointer uppercase"
                  >
                    {t("following")} ({profile.followingCount})
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="followers-list">
                  <FollowersTabContent userId={user.id} searchQuery={searchQuery} />
                </TabsContent>
                <TabsContent value="following-list">
                  <FollowingTabContent userId={user.id} searchQuery={searchQuery} />
                </TabsContent>
              </Tabs>
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  )
}
